import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { shortRef } from '@/lib/checkinRef';

export interface ManifestRow {
  idx: number; passenger_id: string; booking_number: string; first_name: string; last_name: string;
  blank: boolean; stop: string; company: string; flight?: string; checked_in: boolean;
}

const Blank = () => <span className="inline-block w-14 sm:w-24 border-b border-slate-400 align-middle" aria-hidden="true" />;

// Sjåførformatet (☐ · Nr · Fornavn · Etternavn · Stopp · Flynr · Selskap) med avhuking på nett. Tomme seter = nummererte blanke rader.
export function DriverManifestTable({ rows, pending, onToggle }: {
  rows: ManifestRow[]; pending: Record<string, boolean>; onToggle: (row: ManifestRow, checked: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-xl border bg-white" data-testid="pl-table-wrap">
      <table className="w-full text-xs sm:text-sm" data-testid="pl-table">
        <thead className="bg-indigo-50 text-[10px] sm:text-[11px] uppercase tracking-wide text-slate-700">
          <tr>
            <th className="px-1.5 sm:px-2 py-2 w-10 text-center" title={t('driverManifest.avhuk', 'Avhuk.')}>☐</th>
            <th className="px-1.5 sm:px-2 py-2 w-10 text-right">{t('driverManifest.nr', 'Nr.')}</th>
            <th className="px-1.5 sm:px-2 py-2 text-left">{t('driverManifest.fornavn', 'Fornavn')}</th>
            <th className="px-1.5 sm:px-2 py-2 text-left">{t('driverManifest.etternavn', 'Etternavn')}</th>
            <th className="px-1.5 sm:px-2 py-2 text-left">{t('driverManifest.stopp', 'Stopp')}</th>
            <th className="px-1.5 sm:px-2 py-2 text-left">{t('driverManifest.flynr', 'Flynr.')}</th>
            <th className="px-1.5 sm:px-2 py-2 text-left">{t('driverManifest.selskap', 'Selskap')}</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500 italic" data-testid="pl-empty">{t('driverManifest.ingenPassasjerer', 'Ingen passasjerer registrert.')}</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.passenger_id} className={`${r.checked_in ? 'bg-emerald-50/70' : ''} ${r.blank ? 'text-slate-400' : ''}`}
              data-testid={`pl-row-${r.passenger_id}`} data-checked={r.checked_in ? 'true' : 'false'}>
              <td className="px-1.5 sm:px-2 py-2 text-center">
                <label className="inline-flex items-center justify-center h-9 w-9 -m-2 cursor-pointer">
                  {pending[r.passenger_id]
                    ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    : <Checkbox checked={r.checked_in} onCheckedChange={(v: any) => onToggle(r, !!v)} className="h-5 w-5" aria-label={`${r.first_name} ${r.last_name}`.trim() || `#${r.idx}`} data-testid={`pl-check-${r.passenger_id}`} />}
                </label>
              </td>
              <td className="px-1.5 sm:px-2 py-2 text-right font-mono text-slate-600">{r.idx}</td>
              <td className={`px-1.5 sm:px-2 py-2 ${r.checked_in ? 'line-through decoration-emerald-500' : ''}`}>{r.blank ? <Blank /> : r.first_name}</td>
              <td className={`px-1.5 sm:px-2 py-2 ${r.checked_in ? 'line-through decoration-emerald-500' : ''}`}>
                {r.blank ? <Blank /> : r.last_name}
                {!r.blank && <span className="ml-1 text-[10px] font-mono text-slate-400 no-underline" data-testid={`pl-ref-${r.passenger_id}`}>#{shortRef(r.passenger_id)}</span>}
              </td>
              <td className="px-1.5 sm:px-2 py-2 text-slate-700 break-words">{r.stop}</td>
              <td className="px-1.5 sm:px-2 py-2 text-slate-700 font-mono text-xs break-words" data-testid={`pl-flight-${r.passenger_id}`}>{r.flight || ''}</td>
              <td className="px-1.5 sm:px-2 py-2 text-slate-700 break-words">{r.company}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
